/**
 * PortalEditor — Create/Edit portal configuration
 *
 * Full portal editor with tabs:
 * - General: name, slug, description, status
 * - Access: roles, access levels
 * - Navigation: sidebar items, ordering
 * - Pages: manage dynamic pages
 * - Modules & Features: toggle enabled modules/features
 * - Translations: per-language text widget translations
 * - Theme: overrides (primary color, brand name)
 *
 * Uses FullPageDetailTemplate for layout, header, and KDSnackbar.
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Autocomplete,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Pages as PagesIcon,
} from "@mui/icons-material";
import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import FullPageDetailTemplate from "templates/FullPageDetailTemplate";
import { useT } from "context/I18nContext";
import usePortals from "hooks/usePortals";
import kadminConfig from "kadmin.config";
import logger from "services/logger";

// Available languages
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];

// Available roles
const AVAILABLE_ROLES = kadminConfig.roles || ["admin", "user"];

// Icon options for navigation
const NAV_ICONS = [
  "dashboard", "home", "article", "description", "analytics",
  "bar_chart", "pie_chart", "table_chart", "data_usage",
  "people", "person", "group", "settings", "tune",
  "notifications", "mail", "chat", "forum", "message",
  "folder", "inventory_2", "shopping_cart", "payment", "receipt",
  "star", "favorite", "bookmark", "flag", "label",
  "video_library", "image", "music_note", "movie",
  "cloud", "storage", "memory", "devices", "security",
  "build", "code", "bug_report", "speed", "timeline",
];

function PortalEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { palette } = useTheme();
  const t = useT();

  // Local snackbar state — rendered by FullPageDetailTemplate via KDSnackbar
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", title: "", message: "" });
  const showSnackbar = useCallback(
    (severity, title, message) => setSnackbar({ open: true, severity, title, message }),
    []
  );
  const {
    getPortal,
    createPortal,
    updatePortal,
    getTranslations,
    setTranslations,
  } = usePortals();

  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!isNew);

  // Portal form state
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    isActive: true,
    roles: [],
    accessLevels: {},
    defaultLanguage: "en",
    supportedLanguages: ["en"],
    enabledModules: [],
    enabledFeatures: [],
    navigation: [],
    breadcrumbTemplate: "{portalName} > {pageName}",
    pages: [],
    themeOverrides: {
      primaryColor: "#1976d2",
      brandName: "",
    },
  });

  // Translations state (per-language)
  const [translations, setTranslationsState] = useState({});
  const [editingTransLang, setEditingTransLang] = useState("en");

  // Load existing portal
  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const portal = await getPortal(id);
          if (!portal) {
            showSnackbar("error", "Error", "Portal not found");
            navigate("/admin/portals");
            return;
          }
          setForm({
            name: portal.name || "",
            slug: portal.slug || "",
            description: portal.description || "",
            isActive: portal.isActive !== false,
            roles: portal.roles || [],
            accessLevels: portal.accessLevels || {},
            defaultLanguage: portal.defaultLanguage || "en",
            supportedLanguages: portal.supportedLanguages || ["en"],
            enabledModules: portal.enabledModules || [],
            enabledFeatures: portal.enabledFeatures || [],
            navigation: portal.navigation || [],
            breadcrumbTemplate: portal.breadcrumbTemplate || "{portalName} > {pageName}",
            pages: portal.pages || [],
            themeOverrides: portal.themeOverrides || { primaryColor: "#1976d2", brandName: "" },
          });

          // Load translations
          try {
            const trans = await getTranslations(id);
            setTranslationsState(trans);
          } catch {
            // No translations yet
          }
        } catch (err) {
          logger.error("[PortalEditor]", "Load error:", err.message);
          showSnackbar("error", "Error", "Failed to load portal");
        } finally {
          setInitialLoading(false);
        }
      })();
    }
  }, [id, isNew, getPortal, getTranslations, navigate, showSnackbar]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSlugify = useCallback((name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) {
      showSnackbar("error", "Validation", "Portal name is required");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        slug: form.slug || handleSlugify(form.name),
      };

      let savedPortal;
      if (isNew) {
        savedPortal = await createPortal(data);
        showSnackbar("success", "Success", t("portals.created") || "Portal created");
        navigate(`/admin/portals/${savedPortal.id}`);
      } else {
        savedPortal = await updatePortal(id, data);
        showSnackbar("success", "Success", t("portals.saved") || "Portal saved");
      }

      // Save translations
      if (!isNew && Object.keys(translations).length > 0) {
        for (const [lang, messages] of Object.entries(translations)) {
          await setTranslations(id, lang, messages);
        }
      }
    } catch (err) {
      logger.error("[PortalEditor]", "Save error:", err.message);
      showSnackbar("error", "Error", err.message || "Failed to save portal");
    } finally {
      setSaving(false);
    }
  }, [form, isNew, id, translations, createPortal, updatePortal, setTranslations, handleSlugify, navigate, showSnackbar, t]);

  // Navigation item management
  const addNavItem = useCallback(() => {
    const newItem = {
      id: `nav-${Date.now()}`,
      label: "New Page",
      labelKey: "",
      icon: "article",
      route: "",
      pageId: "",
      caslAction: "",
      caslSubject: "",
    };
    updateField("navigation", [...form.navigation, newItem]);
  }, [form.navigation, updateField]);

  const updateNavItem = useCallback(
    (idx, field, value) => {
      const updated = [...form.navigation];
      updated[idx] = { ...updated[idx], [field]: value };
      updateField("navigation", updated);
    },
    [form.navigation, updateField]
  );

  const removeNavItem = useCallback(
    (idx) => {
      updateField("navigation", form.navigation.filter((_, i) => i !== idx));
    },
    [form.navigation, updateField]
  );

  // Translation management
  const updateTranslation = useCallback(
    (key, value) => {
      setTranslationsState((prev) => ({
        ...prev,
        [editingTransLang]: {
          ...(prev[editingTransLang] || {}),
          [key]: value,
        },
      }));
    },
    [editingTransLang]
  );

  const addTranslationKey = useCallback(() => {
    const key = prompt("Translation key (e.g., portal.welcome.title):");
    if (!key) return;
    updateTranslation(key, "");
  }, [updateTranslation]);

  // Style helpers — derived from theme palette
  const textPrimary = palette.text.main;
  const textSecondary = palette.text.secondary;
  const borderColor = palette.divider;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: palette.text.main,
      "& fieldset": { borderColor: palette.inputBorderColor },
      "&:hover fieldset": { borderColor: palette.inputBorderHoverColor },
    },
    "& .MuiInputLabel-root": { color: palette.text.secondary },
  };

  // Action buttons for the header
  const actionButtons = (
    <KDButton
      variant="contained"
      size="small"
      onClick={handleSave}
      disabled={saving}
      sx={{
        color: palette.white.main,
        backgroundColor: palette.headerOverlay.button,
        borderRadius: "10px",
        "&:hover": { backgroundColor: palette.headerOverlay.buttonHover },
      }}
    >
      {saving ? (
        <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
      ) : (
        <SaveIcon sx={{ mr: 0.5, fontSize: 18 }} />
      )}
      {saving ? "Saving..." : t("common.save") || "Save"}
    </KDButton>
  );

  // Chips for header
  const chips = [];
  if (!isNew && form.slug) {
    chips.push({ label: `/${form.slug}`, bgcolor: palette.headerOverlay.button });
  }
  if (!isNew) {
    chips.push({
      label: form.isActive ? "Active" : "Inactive",
      bgcolor: form.isActive
        ? `${palette.success.main}4D`
        : `${palette.error.main}4D`,
    });
  }

  return (
    <FullPageDetailTemplate
      title={isNew ? (t("portals.createTitle") || "Create Portal") : form.name || t("portals.title")}
      subtitle={!isNew && form.description ? form.description : undefined}
      chips={chips.length > 0 ? chips : undefined}
      headerColor="info"
      backRoute="/admin/portals"
      loading={initialLoading}
      actions={actionButtons}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
    >
      {/* ── Content Card with Tabs ── */}
      <Card
        sx={{
          overflow: "visible",
          background: palette.background.glass,
          backdropFilter: "blur(10px)",
          border: `1px solid ${palette.background.glassBorder}`,
          borderRadius: "16px",
        }}
      >

        {/* ── Tabs ── */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            mt: 2,
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
          }}
        >
          <Tab label={t("portals.tabGeneral") || "General"} />
          <Tab label={t("portals.tabAccess") || "Access"} />
          <Tab label={t("portals.tabNavigation") || "Navigation"} />
          <Tab label={t("portals.tabPages") || "Pages"} />
          <Tab label={t("portals.tabModules") || "Modules"} />
          <Tab label={t("portals.tabTranslations") || "Translations"} />
          <Tab label={t("portals.tabTheme") || "Theme"} />
        </Tabs>

        {/* ── Tab content ── */}
        <Box p={3}>
          {/* ==================== GENERAL TAB ==================== */}
          {tab === 0 && (
            <KDBox display="flex" flexDirection="column" gap={3}>
              <TextField
                label="Portal Name"
                value={form.name}
                onChange={(e) => {
                  updateField("name", e.target.value);
                  if (isNew) updateField("slug", handleSlugify(e.target.value));
                }}
                fullWidth
                sx={inputSx}
              />
              <TextField
                label="Slug (URL path)"
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                fullWidth
                helperText={`URL: /portal/${form.slug || "..."}`}
                sx={inputSx}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={inputSx}
              />
              <TextField
                label="Breadcrumb Template"
                value={form.breadcrumbTemplate}
                onChange={(e) => updateField("breadcrumbTemplate", e.target.value)}
                fullWidth
                helperText="Use {portalName} and {pageName} as placeholders"
                sx={inputSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => updateField("isActive", e.target.checked)}
                    color="success"
                  />
                }
                label={<KDTypography variant="body2" sx={{ color: textPrimary }}>Active</KDTypography>}
              />
            </KDBox>
          )}

          {/* ==================== ACCESS TAB ==================== */}
          {tab === 1 && (
            <KDBox display="flex" flexDirection="column" gap={3}>
              <Autocomplete
                multiple
                options={AVAILABLE_ROLES}
                value={form.roles}
                onChange={(_, newValue) => updateField("roles", newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Allowed Roles" sx={inputSx} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
              />

              <KDTypography variant="body2" sx={{ color: textSecondary }}>
                Access levels per role (optional — leave empty to allow all levels)
              </KDTypography>

              {form.roles.map((role) => (
                <KDBox key={role} display="flex" alignItems="center" gap={2}>
                  <KDTypography variant="body2" sx={{ color: textPrimary, minWidth: 100 }}>
                    {role}:
                  </KDTypography>
                  <TextField
                    label={`Access levels for ${role} (comma-separated)`}
                    value={(form.accessLevels[role] || []).join(", ")}
                    onChange={(e) => {
                      const levels = e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      updateField("accessLevels", { ...form.accessLevels, [role]: levels });
                    }}
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </KDBox>
              ))}

              <Divider sx={{ borderColor }} />

              <Autocomplete
                options={LANGUAGES.map((l) => l.code)}
                value={form.defaultLanguage}
                onChange={(_, newValue) => updateField("defaultLanguage", newValue || "en")}
                getOptionLabel={(code) => LANGUAGES.find((l) => l.code === code)?.label || code}
                renderInput={(params) => (
                  <TextField {...params} label="Default Language" sx={inputSx} />
                )}
              />

              <Autocomplete
                multiple
                options={LANGUAGES.map((l) => l.code)}
                value={form.supportedLanguages}
                onChange={(_, newValue) => updateField("supportedLanguages", newValue)}
                getOptionLabel={(code) => LANGUAGES.find((l) => l.code === code)?.label || code}
                renderInput={(params) => (
                  <TextField {...params} label="Supported Languages" sx={inputSx} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={LANGUAGES.find((l) => l.code === option)?.label || option}
                      size="small"
                    />
                  ))
                }
              />
            </KDBox>
          )}

          {/* ==================== NAVIGATION TAB ==================== */}
          {tab === 2 && (
            <KDBox>
              <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <KDTypography variant="h6" sx={{ color: textPrimary }}>
                  Navigation Items
                </KDTypography>
                <KDButton size="small" variant="outlined" color="info" startIcon={<AddIcon />} onClick={addNavItem}>
                  Add Item
                </KDButton>
              </KDBox>

              {form.navigation.length === 0 ? (
                <KDTypography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 4 }}>
                  No navigation items yet. Add items to build the portal sidebar.
                </KDTypography>
              ) : (
                <KDBox display="flex" flexDirection="column" gap={2}>
                  {form.navigation.map((navItem, idx) => (
                    <Card
                      key={navItem.id}
                      sx={{
                        p: 2,
                        background: palette.background.cardInner,
                        border: `1px solid ${borderColor}`,
                        borderRadius: "8px",
                      }}
                    >
                      <KDBox display="flex" gap={2} flexWrap="wrap">
                        <TextField
                          label="Label"
                          value={navItem.label}
                          onChange={(e) => updateNavItem(idx, "label", e.target.value)}
                          size="small"
                          sx={{ ...inputSx, flex: 1, minWidth: 150 }}
                        />
                        <TextField
                          label="Label Key (i18n)"
                          value={navItem.labelKey || ""}
                          onChange={(e) => updateNavItem(idx, "labelKey", e.target.value)}
                          size="small"
                          sx={{ ...inputSx, flex: 1, minWidth: 150 }}
                        />
                        <Autocomplete
                          options={NAV_ICONS}
                          value={navItem.icon || "article"}
                          onChange={(_, v) => updateNavItem(idx, "icon", v || "article")}
                          renderInput={(params) => (
                            <TextField {...params} label="Icon" size="small" sx={{ ...inputSx, width: 150 }} />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Icon sx={{ mr: 1, fontSize: 18, color: textSecondary }}>{option}</Icon>
                              {option}
                            </li>
                          )}
                          sx={{ width: 180 }}
                        />
                        <TextField
                          label="Route"
                          value={navItem.route || ""}
                          onChange={(e) => updateNavItem(idx, "route", e.target.value)}
                          size="small"
                          helperText="Leave empty for dynamic page"
                          sx={{ ...inputSx, flex: 1, minWidth: 200 }}
                        />
                        <TextField
                          label="Page ID"
                          value={navItem.pageId || ""}
                          onChange={(e) => updateNavItem(idx, "pageId", e.target.value)}
                          size="small"
                          helperText="Links to a dynamic page"
                          sx={{ ...inputSx, flex: 1, minWidth: 150 }}
                        />
                      </KDBox>
                      <KDBox display="flex" gap={2} mt={1.5} flexWrap="wrap" alignItems="center">
                        <TextField
                          label="CASL Action (optional)"
                          value={navItem.caslAction || ""}
                          onChange={(e) => updateNavItem(idx, "caslAction", e.target.value)}
                          size="small"
                          sx={{ ...inputSx, width: 150 }}
                        />
                        <TextField
                          label="CASL Subject (optional)"
                          value={navItem.caslSubject || ""}
                          onChange={(e) => updateNavItem(idx, "caslSubject", e.target.value)}
                          size="small"
                          sx={{ ...inputSx, width: 150 }}
                        />
                        <Box flex={1} />
                        <Tooltip title="Delete item">
                          <IconButton size="small" onClick={() => removeNavItem(idx)} sx={{ color: palette.error.main }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </KDBox>
                    </Card>
                  ))}
                </KDBox>
              )}
            </KDBox>
          )}

          {/* ==================== PAGES TAB ==================== */}
          {tab === 3 && (
            <KDBox>
              <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <KDTypography variant="h6" sx={{ color: textPrimary }}>
                  Dynamic Pages
                </KDTypography>
                <KDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (isNew) {
                      showSnackbar("info", "Info", "Save the portal first, then add pages");
                      return;
                    }
                    navigate(`/admin/portals/${id}/pages/new`);
                  }}
                >
                  Add Page
                </KDButton>
              </KDBox>

              {form.pages.length === 0 ? (
                <KDTypography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 4 }}>
                  No dynamic pages yet. Pages allow you to compose widgets into custom layouts.
                </KDTypography>
              ) : (
                <List>
                  {form.pages.map((page) => (
                    <ListItem
                      key={page.id}
                      sx={{
                        border: `1px solid ${borderColor}`,
                        borderRadius: "8px",
                        mb: 1,
                        bgcolor: palette.background.cardInner,
                      }}
                    >
                      <ListItemIcon>
                        <PagesIcon sx={{ color: textSecondary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <KDTypography variant="body1" sx={{ color: textPrimary }}>
                            {page.name}
                          </KDTypography>
                        }
                        secondary={
                          <KDTypography variant="caption" sx={{ color: textSecondary }}>
                            /{page.slug} · {Object.keys(page.widgets || {}).length} widgets
                          </KDTypography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit page">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/portals/${id}/pages/${page.id}`)}
                            sx={{ color: textSecondary }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </KDBox>
          )}

          {/* ==================== MODULES TAB ==================== */}
          {tab === 4 && (
            <KDBox display="flex" flexDirection="column" gap={3}>
              <KDBox>
                <KDTypography variant="h6" sx={{ color: textPrimary, mb: 1 }}>
                  Enabled Modules
                </KDTypography>
                <KDTypography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                  Select which modules are accessible within this portal.
                </KDTypography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={["dashboard", "users", "samples", "roles", "invites", "notifications", "settings", "portals"]}
                  value={form.enabledModules}
                  onChange={(_, newValue) => updateField("enabledModules", newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Modules" sx={inputSx} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                    ))
                  }
                />
              </KDBox>

              <Divider sx={{ borderColor }} />

              <KDBox>
                <KDTypography variant="h6" sx={{ color: textPrimary, mb: 1 }}>
                  Enabled Features
                </KDTypography>
                <KDTypography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                  Toggle feature flags for this portal.
                </KDTypography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={["darkMode", "rtl", "impersonation", "apiKeys", "sso", "ai", "billing", "export", "import"]}
                  value={form.enabledFeatures}
                  onChange={(_, newValue) => updateField("enabledFeatures", newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Features" sx={inputSx} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                    ))
                  }
                />
              </KDBox>
            </KDBox>
          )}

          {/* ==================== TRANSLATIONS TAB ==================== */}
          {tab === 5 && (
            <KDBox>
              <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <KDTypography variant="h6" sx={{ color: textPrimary }}>
                  Portal Translations
                </KDTypography>
                <KDBox display="flex" gap={1} alignItems="center">
                  <Autocomplete
                    options={form.supportedLanguages}
                    value={editingTransLang}
                    onChange={(_, v) => setEditingTransLang(v || "en")}
                    getOptionLabel={(code) => LANGUAGES.find((l) => l.code === code)?.label || code}
                    renderInput={(params) => (
                      <TextField {...params} label="Language" size="small" sx={{ ...inputSx, width: 150 }} />
                    )}
                    disableClearable
                    sx={{ width: 180 }}
                  />
                  <KDButton size="small" variant="outlined" color="info" startIcon={<AddIcon />} onClick={addTranslationKey}>
                    Add Key
                  </KDButton>
                </KDBox>
              </KDBox>

              {isNew ? (
                <KDTypography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 4 }}>
                  Save the portal first, then manage translations.
                </KDTypography>
              ) : (
                <KDBox display="flex" flexDirection="column" gap={1.5}>
                  {Object.entries(translations[editingTransLang] || {}).length === 0 ? (
                    <KDTypography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 4 }}>
                      No translations for {LANGUAGES.find((l) => l.code === editingTransLang)?.label || editingTransLang} yet.
                    </KDTypography>
                  ) : (
                    Object.entries(translations[editingTransLang] || {}).map(([key, value]) => (
                      <KDBox key={key} display="flex" gap={2} alignItems="center">
                        <KDTypography
                          variant="caption"
                          sx={{ color: textSecondary, minWidth: 200, fontFamily: "monospace" }}
                        >
                          {key}
                        </KDTypography>
                        <TextField
                          value={value}
                          onChange={(e) => updateTranslation(key, e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </KDBox>
                    ))
                  )}
                </KDBox>
              )}
            </KDBox>
          )}

          {/* ==================== THEME TAB ==================== */}
          {tab === 6 && (
            <KDBox display="flex" flexDirection="column" gap={3}>
              <TextField
                label="Brand Name Override"
                value={form.themeOverrides.brandName || ""}
                onChange={(e) =>
                  updateField("themeOverrides", { ...form.themeOverrides, brandName: e.target.value })
                }
                fullWidth
                helperText="Displayed in the sidenav when this portal is active"
                sx={inputSx}
              />
              <KDBox display="flex" gap={2} alignItems="center">
                <TextField
                  label="Primary Color"
                  value={form.themeOverrides.primaryColor || "#1976d2"}
                  onChange={(e) =>
                    updateField("themeOverrides", { ...form.themeOverrides, primaryColor: e.target.value })
                  }
                  sx={{ ...inputSx, width: 200 }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: form.themeOverrides.primaryColor || "#1976d2",
                    border: `1px solid ${borderColor}`,
                  }}
                />
                <input
                  type="color"
                  value={form.themeOverrides.primaryColor || "#1976d2"}
                  onChange={(e) =>
                    updateField("themeOverrides", { ...form.themeOverrides, primaryColor: e.target.value })
                  }
                  style={{ width: 40, height: 40, border: "none", cursor: "pointer" }}
                />
              </KDBox>
            </KDBox>
          )}
        </Box>
      </Card>
    </FullPageDetailTemplate>
  );
}

export default PortalEditor;
